'use client';

import { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useQuery, gql } from '@apollo/client';
import { GET_DESIGN_LED } from '@/graphql/queries';


// ── Helper to build media URLs from S3 file objects ────────────────────
const buildMediaUrl = (fileObj) => {
  if (!fileObj || !fileObj.path) return null;
  const bucket = fileObj.bucket || 'jewel-ecom';
  const region = 'ap-south-1';
  return `https://${bucket}.s3.${region}.amazonaws.com/${fileObj.path}`;
};

export default function DesignLedJewellery() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // ── Fetch Design Led data ────────────────────────────────────────────
  const { data } = useQuery(GET_DESIGN_LED, {
    variables: { where: { type: { _eq: 'DESIGN_LED' } } },
    fetchPolicy: 'cache-and-network',
  });

  // ── Transform GraphQL data to component structure ──────────────────────
  useEffect(() => {
    if (data?.tenant_org_web_configuration?.[0]) {
      const metas = data.tenant_org_web_configuration[0].web_configuration_meta || [];
      
      const transformedCategories = metas.map((meta, index) => ({
        id: index + 1,
        title: meta.title || 'Category',
        lifestyleImage: buildMediaUrl(meta.file) || '',
        mobileImage: buildMediaUrl(meta.file) || '', // Fallback to file if no separate mobile image
        productImage: buildMediaUrl(meta.icon) || '',
        link: meta.redirection_url || '/',
      }));

      setCategories(transformedCategories);
      setLoading(false);
    }
  }, [data]);

  // ── Auto-advance carousel on mobile ──────────────────────────────────
  const advance = useCallback(
    () => setActiveIndex((prev) => (prev + 1) % categories.length),
    [categories.length]
  );

  useEffect(() => {
    if (categories.length === 0) return;
    const timer = setInterval(advance, 2500);
    return () => clearInterval(timer);
  }, [advance, categories.length]);

  // ── Loading state ────────────────────────────────────────────────────
  if (loading || categories.length === 0) {
    return null;
  }

  return (
    <div className="w-full py-10 md:py-16">
      <div className="max-w-7xl mx-auto px-4 md:px-5">

        <h2 className="text-center text-2xl md:text-3xl font-semibold text-gray-700 mb-8 md:mb-12 tracking-tight">
          Design Led Jewellery
        </h2>

        {/* ── DESKTOP ── */}
        <div className="hidden md:grid grid-cols-3 gap-8">
          {categories.map((cat) => (
            <Link key={cat.id} href={cat.link} className="relative cursor-pointer group">
              <div className="relative w-full h-[500px] rounded-2xl overflow-hidden mb-[-60px]">
                <Image
                  src={cat.lifestyleImage}
                  alt={cat.title}
                  fill
                  sizes="(max-width: 1280px) 33vw, 420px"
                  className="object-cover"
                />
              </div>
              <div className="relative z-10 flex justify-center mb-5">
                <div className="relative w-48 h-48 bg-white rounded-2xl shadow-lg overflow-hidden group-hover:-translate-y-1 transition-transform duration-300 will-change-transform">
                  <Image
                    src={cat.productImage}
                    alt={cat.title}
                    fill
                    sizes="192px"
                    className="object-cover"
                  />
                </div>
              </div>
              <h3 className="text-center text-base font-medium text-gray-400 tracking-wide">
                {cat.title}
              </h3>
            </Link>
          ))}
        </div>

        {/* ── MOBILE ── */}
        <div className="block md:hidden">
          <div className="relative w-full pb-20">

            {/* Background images — CSS opacity crossfade */}
            <Link 
              href={categories[activeIndex].link} 
              className="relative w-full rounded-2xl overflow-hidden block" 
              style={{ height: '300px' }}
            >
              {categories.map((cat, i) => (
                <div
                  key={cat.id}
                  className="absolute inset-0 transition-opacity duration-700 will-change-[opacity]"
                  style={{ opacity: activeIndex === i ? 1 : 0 }}
                >
                  <Image
                    src={cat.mobileImage}
                    alt={cat.title}
                    fill
                    sizes="100vw"
                    className="object-cover object-top"
                    priority={i === 0}
                  />
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-black/10 to-transparent" />
            </Link>

            {/* Thumbnail cards */}
            <div className="absolute left-0 right-0 px-3 flex gap-2" style={{ bottom: '0px' }}>
              {categories.map((cat, i) => (
                <Link
                  key={cat.id}
                  href={cat.link}
                  onClick={() => setActiveIndex(i)}
                  className={`flex-1 bg-white rounded-xl overflow-hidden shadow-xl transition-all duration-300 will-change-transform ${
                    activeIndex === i ? 'ring-2 ring-amber-900 scale-105' : 'opacity-90'
                  }`}
                >
                  <div className="relative w-full" style={{ height: '80px' }}>
                    <Image
                      src={cat.productImage}
                      alt={cat.title}
                      fill
                      sizes="30vw"
                      className="object-contain p-1"
                    />
                  </div>
                  <p className="text-center text-[9px] font-medium text-gray-500 py-1.5 px-1 truncate">
                    {cat.title}
                  </p>
                </Link>
              ))}
            </div>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-3">
            {categories.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIndex(i)}
                aria-label={`Go to slide ${i + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  activeIndex === i ? 'w-5 h-2 bg-amber-700' : 'w-2 h-2 bg-gray-300'
                }`}
              />
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}